import {
  raycastAABB,
  type AABB,
} from "../math/aabb.js";
import type { Vec3 } from "../math/vec3.js";
import type { MapDef, MapTerrain, MaterialId } from "../maps/types.js";
import { terrainHeightAt, terrainQuadIsHole } from "../maps/terrain.js";
import { mapCollisionSolids } from "../maps/glass.js";

/**
 * Static collision world: axis-aligned solids plus an optional heightfield,
 * everything clamped to `bounds`. All queries are pure and allocation-light;
 * the geometry is fixed after `buildCollisionWorld` — only `setSolidEnabled`
 * moves, and it only ever switches one solid off or back on.
 */

export interface RayHit {
  /** Distance along the ray, in units of the `dir` passed in. */
  dist: number;
  /** Index into the `solids` array given to `buildCollisionWorld`. */
  boxIndex: number;
  /** Material at the hit. Present for terrain; boxes are indexed by `boxIndex`. */
  material?: MaterialId;
  /** Outward face normal of the surface that was hit. */
  normal: Vec3;
}

export interface CollisionWorld {
  /**
   * Casts a ray. Pass a UNIT `dir` for distances in metres — distances are
   * measured in units of `dir`'s length. Returns the nearest hit, or `null`.
   */
  raycast(origin: Vec3, dir: Vec3, maxDist: number): RayHit | null;
  /**
   * Pushes a vertical capsule out of any geometry it overlaps.
   * @param pos    FEET position (capsule bottom), not the centre
   * @param radius capsule radius
   * @param height total capsule height, feet to crown
   * @returns the corrected feet position (a new Vec3; `pos` is untouched)
   */
  resolveCapsule(pos: Vec3, radius: number, height: number): Vec3;
  /** True when solid geometry sits immediately under the given feet position. */
  groundCheck(pos: Vec3, radius: number): boolean;
  /**
   * Switches one solid off (a broken pane) or back on. Out-of-range indices
   * are ignored.
   */
  setSolidEnabled(index: number, enabled: boolean): void;
}

/** Broadphase cell size, metres. */
export const CELL_SIZE = 4;

/** How far below the feet `groundCheck` looks, metres. */
export const GROUND_EPS = 0.05;

/** Depth below which a contact is treated as "just touching" and ignored. */
const SKIN = 1e-4;

/** Height samples are wire-quantized to centimetres; interpolation can leave
 * a sub-millimetre difference between a rounded spawn and its surface. */
const TERRAIN_SKIN = 5e-4;

/** Max push-out passes per `resolveCapsule` call. */
const RESOLVE_ITERATIONS = 4;

/**
 * How far outside a box face the seam probe samples (`faceIsInternal`) — far
 * smaller than any real gap, far larger than float noise.
 */
const SEAM_PROBE = 1e-3;

/** Scratch for the six escape depths of the deep-penetration fallback. */
const penBuf = new Float64Array(6);
/** Whether the matching escape direction leads into another solid. */
const penBlocked = [false, false, false, false, false, false];

export function buildCollisionWorld(
  solids: readonly AABB[],
  bounds: AABB,
  terrain?: MapTerrain,
): CollisionWorld {
  terrain ??= (solids as readonly AABB[] & { terrain?: MapTerrain }).terrain;
  const boxes: AABB[] = solids.map((b) => ({
    min: { x: b.min.x, y: b.min.y, z: b.min.z },
    max: { x: b.max.x, y: b.max.y, z: b.max.z },
  }));
  // 1 = switched off. Zero-filled, so every solid starts enabled.
  const off = new Uint8Array(boxes.length);

  // --- Grid extent: the union of `bounds` and every solid, so no box can ever
  // fall outside the grid and get mis-bucketed. -----------------------------
  let gMinX = bounds.min.x;
  let gMinZ = bounds.min.z;
  let gMaxX = bounds.max.x;
  let gMaxZ = bounds.max.z;
  for (const b of boxes) {
    if (b.min.x < gMinX) gMinX = b.min.x;
    if (b.min.z < gMinZ) gMinZ = b.min.z;
    if (b.max.x > gMaxX) gMaxX = b.max.x;
    if (b.max.z > gMaxZ) gMaxZ = b.max.z;
  }
  // A little slack so points exactly on the far edge still land in a cell.
  gMinX -= CELL_SIZE;
  gMinZ -= CELL_SIZE;
  gMaxX += CELL_SIZE;
  gMaxZ += CELL_SIZE;

  const nx = Math.max(1, Math.ceil((gMaxX - gMinX) / CELL_SIZE));
  const nz = Math.max(1, Math.ceil((gMaxZ - gMinZ) / CELL_SIZE));

  const cells: number[][] = new Array<number[]>(nx * nz);
  for (let i = 0; i < cells.length; i++) cells[i] = [];

  const cellX = (x: number): number => {
    const c = Math.floor((x - gMinX) / CELL_SIZE);
    return c < 0 ? 0 : c >= nx ? nx - 1 : c;
  };
  const cellZ = (z: number): number => {
    const c = Math.floor((z - gMinZ) / CELL_SIZE);
    return c < 0 ? 0 : c >= nz ? nz - 1 : c;
  };

  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i] as AABB;
    const x0 = cellX(b.min.x);
    const x1 = cellX(b.max.x);
    const z0 = cellZ(b.min.z);
    const z1 = cellZ(b.max.z);
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        (cells[cz * nx + cx] as number[]).push(i);
      }
    }
  }

  /** Whole-grid box, used to clip rays that start outside the world. */
  const gridBox: AABB = {
    min: { x: gMinX, y: -Infinity, z: gMinZ },
    max: { x: gMaxX, y: Infinity, z: gMaxZ },
  };

  // Stamp buffer so a box straddling several cells is only tested once per query.
  const stamp = new Int32Array(boxes.length);
  let stampCounter = 0;

  /** Next unique stamp, with a wrap guard so the buffer can never alias. */
  const nextMark = (): number => {
    if (stampCounter >= 0x7ffffffe) {
      stamp.fill(0);
      stampCounter = 0;
    }
    stampCounter++;
    return stampCounter;
  };

  /**
   * Point-in-any-solid test that does NOT touch the shared candidate/stamp
   * scratch, so it is safe inside a `gatherCandidates` walk.
   */
  function pointInSolid(x: number, y: number, z: number, skip: number): boolean {
    const bucket = cells[cellZ(z) * nx + cellX(x)];
    if (bucket === undefined) return false;
    for (let k = 0; k < bucket.length; k++) {
      const bi = bucket[k] as number;
      if (bi === skip || off[bi] === 1) continue;
      const b = boxes[bi] as AABB;
      if (
        x >= b.min.x &&
        x <= b.max.x &&
        y >= b.min.y &&
        y <= b.max.y &&
        z >= b.min.z &&
        z <= b.max.z
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * True when the space just beyond box `bi`'s face on `axis`/`sign`, at the
   * contact point, is filled by another solid — an INTERNAL SEAM. Two flush
   * boxes share a face that is not part of the union's surface and must never
   * push the capsule; without this, walking across two coplanar slabs stops the
   * player dead.
   */
  function faceIsInternal(
    bi: number,
    b: AABB,
    axis: number,
    sign: number,
    qx: number,
    qy: number,
    qz: number,
  ): boolean {
    let x: number;
    let y: number;
    let z: number;
    if (axis === 0) {
      x = sign > 0 ? b.max.x + SEAM_PROBE : b.min.x - SEAM_PROBE;
      y = insideRange(qy, b.min.y, b.max.y);
      z = insideRange(qz, b.min.z, b.max.z);
    } else if (axis === 1) {
      x = insideRange(qx, b.min.x, b.max.x);
      y = sign > 0 ? b.max.y + SEAM_PROBE : b.min.y - SEAM_PROBE;
      z = insideRange(qz, b.min.z, b.max.z);
    } else {
      x = insideRange(qx, b.min.x, b.max.x);
      y = insideRange(qy, b.min.y, b.max.y);
      z = sign > 0 ? b.max.z + SEAM_PROBE : b.min.z - SEAM_PROBE;
    }
    return pointInSolid(x, y, z, bi);
  }

  // ---------------------------------------------------------------------
  // Raycast — 2D DDA across the XZ grid, slab test per candidate box.
  // ---------------------------------------------------------------------
  function raycast(origin: Vec3, dir: Vec3, maxDist: number): RayHit | null {
    if ((boxes.length === 0 && terrain === undefined) || maxDist <= 0) return null;
    if (dir.x === 0 && dir.y === 0 && dir.z === 0) return null;

    const tEnter = raycastAABB(origin, dir, maxDist, gridBox);
    if (tEnter === null) return null;

    const mark = nextMark();

    const startX = origin.x + dir.x * tEnter;
    const startZ = origin.z + dir.z * tEnter;
    let cx = cellX(startX);
    let cz = cellZ(startZ);

    const stepX = dir.x > 0 ? 1 : dir.x < 0 ? -1 : 0;
    const stepZ = dir.z > 0 ? 1 : dir.z < 0 ? -1 : 0;

    let tMaxX = Infinity;
    let tDeltaX = Infinity;
    if (stepX !== 0) {
      const boundary = gMinX + (cx + (stepX > 0 ? 1 : 0)) * CELL_SIZE;
      tMaxX = (boundary - origin.x) / dir.x;
      tDeltaX = CELL_SIZE / Math.abs(dir.x);
    }

    let tMaxZ = Infinity;
    let tDeltaZ = Infinity;
    if (stepZ !== 0) {
      const boundary = gMinZ + (cz + (stepZ > 0 ? 1 : 0)) * CELL_SIZE;
      tMaxZ = (boundary - origin.z) / dir.z;
      tDeltaZ = CELL_SIZE / Math.abs(dir.z);
    }

    let bestDist = Infinity;
    let bestIndex = -1;
    const bestNormal: Vec3 = { x: 0, y: 0, z: 0 };
    const scratchNormal: Vec3 = { x: 0, y: 0, z: 0 };

    for (;;) {
      const bucket = cells[cz * nx + cx];
      if (bucket !== undefined) {
        for (let k = 0; k < bucket.length; k++) {
          const bi = bucket[k] as number;
          if (stamp[bi] === mark) continue;
          stamp[bi] = mark;
          if (off[bi] === 1) continue;
          const t = raycastAABB(
            origin,
            dir,
            maxDist,
            boxes[bi] as AABB,
            scratchNormal,
          );
          if (t !== null && t < bestDist) {
            bestDist = t;
            bestIndex = bi;
            bestNormal.x = scratchNormal.x;
            bestNormal.y = scratchNormal.y;
            bestNormal.z = scratchNormal.z;
          }
        }
      }

      // t at which the ray leaves the current cell.
      const tExit = tMaxX < tMaxZ ? tMaxX : tMaxZ;
      // Any box in a later cell can only be hit at t >= tExit.
      if (bestIndex !== -1 && bestDist <= tExit) break;
      if (tExit > maxDist || tExit === Infinity) break;

      if (tMaxX < tMaxZ) {
        cx += stepX;
        if (cx < 0 || cx >= nx) break;
        tMaxX += tDeltaX;
      } else {
        cz += stepZ;
        if (cz < 0 || cz >= nz) break;
        tMaxZ += tDeltaZ;
      }
    }

    if (terrain !== undefined) {
      const terrainHit = raycastTerrain(terrain, origin, dir, maxDist);
      if (terrainHit !== null && terrainHit.dist < bestDist) return terrainHit;
    }
    if (bestIndex === -1) return null;
    return { dist: bestDist, boxIndex: bestIndex, normal: bestNormal };
  }

  // ---------------------------------------------------------------------
  // Candidate gathering for a world-space XZ rectangle.
  // ---------------------------------------------------------------------
  const candidates: number[] = [];
  function gatherCandidates(
    minX: number,
    minZ: number,
    maxX: number,
    maxZ: number,
  ): number[] {
    candidates.length = 0;
    const mark = nextMark();
    const x0 = cellX(minX);
    const x1 = cellX(maxX);
    const z0 = cellZ(minZ);
    const z1 = cellZ(maxZ);
    for (let cz = z0; cz <= z1; cz++) {
      const row = cz * nx;
      for (let cx = x0; cx <= x1; cx++) {
        const bucket = cells[row + cx];
        if (bucket === undefined) continue;
        for (let k = 0; k < bucket.length; k++) {
          const bi = bucket[k] as number;
          if (stamp[bi] === mark) continue;
          stamp[bi] = mark;
          if (off[bi] === 1) continue;
          candidates.push(bi);
        }
      }
    }
    // Sorted so the resolve order never depends on grid traversal order.
    candidates.sort(numericAsc);
    return candidates;
  }

  // ---------------------------------------------------------------------
  // Capsule push-out.
  // ---------------------------------------------------------------------
  function resolveCapsule(pos: Vec3, radius: number, height: number): Vec3 {
    let px = pos.x;
    let py = pos.y;
    let pz = pos.z;

    // Last-ditch guard: a non-finite input would otherwise survive every clamp
    // below and poison the whole simulation. Snap to a legal, deterministic pose.
    if (!Number.isFinite(px)) px = (bounds.min.x + bounds.max.x) * 0.5;
    if (!Number.isFinite(py)) py = bounds.min.y;
    if (!Number.isFinite(pz)) pz = (bounds.min.z + bounds.max.z) * 0.5;

    // Clamp the capsule's core segment so it stays valid for squat capsules.
    const halfSpan = Math.max(0, height * 0.5 - radius);

    for (let iter = 0; iter < RESOLVE_ITERATIONS; iter++) {
      const list = gatherCandidates(
        px - radius,
        pz - radius,
        px + radius,
        pz + radius,
      );
      let moved = false;

      for (let n = 0; n < list.length; n++) {
        const bi = list[n] as number;
        const b = boxes[bi] as AABB;

        // Reject fast on the capsule's own AABB.
        if (
          px + radius <= b.min.x ||
          px - radius >= b.max.x ||
          pz + radius <= b.min.z ||
          pz - radius >= b.max.z ||
          py + height <= b.min.y ||
          py >= b.max.y
        ) {
          continue;
        }

        // Core segment (sphere centres) of the vertical capsule.
        const midY = py + height * 0.5;
        const y0 = midY - halfSpan;
        const y1 = midY + halfSpan;

        // Closest point on the box to the capsule's axis, and the matching
        // point on the axis.
        const qx = px < b.min.x ? b.min.x : px > b.max.x ? b.max.x : px;
        const qz = pz < b.min.z ? b.min.z : pz > b.max.z ? b.max.z : pz;

        let sy: number;
        if (y1 < b.min.y) sy = y1;
        else if (y0 > b.max.y) sy = y0;
        else sy = clamp((b.min.y + b.max.y) * 0.5, y0, y1);
        const qy = sy < b.min.y ? b.min.y : sy > b.max.y ? b.max.y : sy;

        let dx = px - qx;
        let dy = sy - qy;
        let dz = pz - qz;
        let distSq = dx * dx + dy * dy + dz * dz;

        if (distSq > radius * radius) continue; // no contact

        // Internal-seam suppression: a contact "outside" the box on two or
        // three
        // axes is on an EDGE or CORNER and pushes diagonally. When a neighbour
        // is
        // flush against one of those faces, the edge does not exist on the
        // union
        // — drop the components that point into a solid.
        if (distSq > 1e-12) {
          const ox = dx > 0 ? 1 : dx < 0 ? -1 : 0;
          const oy = dy > 0 ? 1 : dy < 0 ? -1 : 0;
          const oz = dz > 0 ? 1 : dz < 0 ? -1 : 0;
          const openAxes =
            (ox !== 0 ? 1 : 0) + (oy !== 0 ? 1 : 0) + (oz !== 0 ? 1 : 0);
          if (openAxes >= 2) {
            let sealed = 0;
            if (ox !== 0 && faceIsInternal(bi, b, 0, ox, qx, qy, qz)) {
              dx = 0;
              sealed++;
            }
            if (oy !== 0 && faceIsInternal(bi, b, 1, oy, qx, qy, qz)) {
              dy = 0;
              sealed++;
            }
            if (oz !== 0 && faceIsInternal(bi, b, 2, oz, qx, qy, qz)) {
              dz = 0;
              sealed++;
            }
            // Every escape route is interior geometry: the neighbouring boxes
            // own this contact, this one has nothing to say about it.
            if (sealed === openAxes) continue;
            if (sealed > 0) {
              distSq = dx * dx + dy * dy + dz * dz;
              // Degenerate leftovers are interior too — never let a masked
              // shallow contact fall through into the deep-penetration branch.
              if (distSq <= 1e-12) continue;
            }
          }
        }

        if (distSq > 1e-12) {
          const dist = Math.sqrt(distSq);
          const push = radius - dist;
          if (push <= SKIN) continue;
          const inv = 1 / dist;
          px += dx * inv * push;
          py += dy * inv * push;
          pz += dz * inv * push;
          moved = true;
        } else {
          // Capsule axis inside the box: minimum-translation push along the
          // smallest-penetration axis. Escapes that lead into another solid are
          // rejected, so a capsule buried in a floor is not squirted out
          // sideways.
          penBuf[0] = b.max.x - (px - radius); // push +X
          penBuf[1] = px + radius - b.min.x; // push -X
          penBuf[2] = b.max.y - py; // push +Y
          penBuf[3] = py + height - b.min.y; // push -Y
          penBuf[4] = b.max.z - (pz - radius); // push +Z
          penBuf[5] = pz + radius - b.min.z; // push -Z

          const cy = insideRange(midY, b.min.y, b.max.y);
          penBlocked[0] = faceIsInternal(bi, b, 0, 1, b.max.x, cy, pz);
          penBlocked[1] = faceIsInternal(bi, b, 0, -1, b.min.x, cy, pz);
          penBlocked[2] = faceIsInternal(bi, b, 1, 1, px, b.max.y, pz);
          penBlocked[3] = faceIsInternal(bi, b, 1, -1, px, b.min.y, pz);
          penBlocked[4] = faceIsInternal(bi, b, 2, 1, px, cy, b.max.z);
          penBlocked[5] = faceIsInternal(bi, b, 2, -1, px, cy, b.min.z);

          let best = Infinity;
          let axis = -1;
          for (let d = 0; d < 6; d++) {
            if (penBlocked[d] === true) continue;
            const v = penBuf[d] as number;
            if (v < best) {
              best = v;
              axis = d;
            }
          }
          if (axis === -1) {
            // Wholly enclosed — take the shallowest escape anyway rather than
            // leaving the capsule stuck inside geometry.
            for (let d = 0; d < 6; d++) {
              const v = penBuf[d] as number;
              if (v < best) {
                best = v;
                axis = d;
              }
            }
          }
          if (best <= SKIN) continue;

          if (axis === 0) px += best;
          else if (axis === 1) px -= best;
          else if (axis === 2) py += best;
          else if (axis === 3) py -= best;
          else if (axis === 4) pz += best;
          else pz -= best;
          moved = true;
        }
      }

      if (!moved) break;
    }

    // The heightfield is a surface, not a stack of slabs: a penetrating capsule
    // is settled onto the sampled height, continuous across cell edges.
    if (terrain !== undefined) {
      const h = terrainHeightAt(terrain, px, pz);
      if (h !== null && h - py > TERRAIN_SKIN && py + height > h) py = h;
    }

    // Keep the capsule inside the playable volume.
    px = clamp(px, bounds.min.x + radius, bounds.max.x - radius);
    pz = clamp(pz, bounds.min.z + radius, bounds.max.z - radius);
    py = clamp(py, bounds.min.y, bounds.max.y - height);

    return { x: px, y: py, z: pz };
  }

  // ---------------------------------------------------------------------
  // Ground probe.
  // ---------------------------------------------------------------------
  function groundCheck(pos: Vec3, radius: number): boolean {
    const minX = pos.x - radius;
    const maxX = pos.x + radius;
    const minZ = pos.z - radius;
    const maxZ = pos.z + radius;
    const minY = pos.y - GROUND_EPS;
    const maxY = pos.y + SKIN;

    if (terrain !== undefined) {
      const h = terrainHeightAt(terrain, pos.x, pos.z);
      if (h !== null && pos.y - h >= -TERRAIN_SKIN && pos.y - h <= GROUND_EPS) return true;
    }

    const list = gatherCandidates(minX, minZ, maxX, maxZ);
    for (let n = 0; n < list.length; n++) {
      const b = boxes[list[n] as number] as AABB;
      if (
        minX < b.max.x &&
        maxX > b.min.x &&
        minZ < b.max.z &&
        maxZ > b.min.z &&
        minY < b.max.y &&
        maxY > b.min.y
      ) {
        return true;
      }
    }
    return false;
  }

  function setSolidEnabled(index: number, enabled: boolean): void {
    if (!Number.isInteger(index) || index < 0 || index >= boxes.length) return;
    off[index] = enabled ? 0 : 1;
  }

  return { raycast, resolveCapsule, groundCheck, setSolidEnabled };
}

/**
 * Builds the complete collision world for a map, including its heightfield and
 * its glass panes — indices match `mapCollisionSolids`.
 */
export function buildMapCollisionWorld(map: MapDef): CollisionWorld {
  return buildCollisionWorld(
    mapCollisionSolids(map).map((s) => s.box),
    map.bounds,
    map.terrain,
  );
}

interface TerrainRayHit extends RayHit {
  material: MaterialId;
}

/** DDA across heightfield quads, with an exact ray/triangle test in each. */
function raycastTerrain(
  terrain: MapTerrain,
  origin: Vec3,
  dir: Vec3,
  maxDist: number,
): TerrainRayHit | null {
  const maxX = terrain.origin.x + (terrain.width - 1) * terrain.cellSize;
  const maxZ = terrain.origin.z + (terrain.depth - 1) * terrain.cellSize;
  let enter = 0;
  let leave = maxDist;
  const clip = (o: number, d: number, lo: number, hi: number): boolean => {
    if (d === 0) return o >= lo && o <= hi;
    let a = (lo - o) / d;
    let b = (hi - o) / d;
    if (a > b) [a, b] = [b, a];
    if (a > enter) enter = a;
    if (b < leave) leave = b;
    return enter <= leave;
  };
  if (!clip(origin.x, dir.x, terrain.origin.x, maxX)) return null;
  if (!clip(origin.z, dir.z, terrain.origin.z, maxZ)) return null;
  if (leave < 0 || enter > maxDist) return null;
  if (enter < 0) enter = 0;

  const cell = terrain.cellSize;
  const nx = terrain.width - 1;
  const nz = terrain.depth - 1;
  const atX = origin.x + dir.x * enter;
  const atZ = origin.z + dir.z * enter;
  let i = Math.min(nx - 1, Math.max(0, Math.floor((atX - terrain.origin.x) / cell)));
  let j = Math.min(nz - 1, Math.max(0, Math.floor((atZ - terrain.origin.z) / cell)));
  const stepX = dir.x > 0 ? 1 : dir.x < 0 ? -1 : 0;
  const stepZ = dir.z > 0 ? 1 : dir.z < 0 ? -1 : 0;
  let nextX = Infinity;
  let deltaX = Infinity;
  if (stepX !== 0) {
    const edge = terrain.origin.x + (i + (stepX > 0 ? 1 : 0)) * cell;
    nextX = (edge - origin.x) / dir.x;
    deltaX = cell / Math.abs(dir.x);
  }
  let nextZ = Infinity;
  let deltaZ = Infinity;
  if (stepZ !== 0) {
    const edge = terrain.origin.z + (j + (stepZ > 0 ? 1 : 0)) * cell;
    nextZ = (edge - origin.z) / dir.z;
    deltaZ = cell / Math.abs(dir.z);
  }

  let best = Infinity;
  const normal: Vec3 = { x: 0, y: 1, z: 0 };
  for (;;) {
    // A hole quad carries no triangles: the ray passes through to whatever
    // solid is below.
    if (!terrainQuadIsHole(terrain, i, j)) {
      const x0 = terrain.origin.x + i * cell;
      const z0 = terrain.origin.z + j * cell;
      const k = j * terrain.width + i;
      const h00 = terrain.heights[k] as number;
      const h10 = terrain.heights[k + 1] as number;
      const h01 = terrain.heights[k + terrain.width] as number;
      const h11 = terrain.heights[k + terrain.width + 1] as number;
      const a = { x: x0, y: h00, z: z0 };
      const b = { x: x0 + cell, y: h10, z: z0 };
      const c = { x: x0, y: h01, z: z0 + cell };
      const d = { x: x0 + cell, y: h11, z: z0 + cell };
      const n0: Vec3 = { x: 0, y: 0, z: 0 };
      const n1: Vec3 = { x: 0, y: 0, z: 0 };
      const t0 = rayTriangle(origin, dir, a, c, b, maxDist, n0);
      const t1 = rayTriangle(origin, dir, b, c, d, maxDist, n1);
      if (t0 !== null && t0 < best) {
        best = t0;
        normal.x = n0.x; normal.y = n0.y; normal.z = n0.z;
      }
      if (t1 !== null && t1 < best) {
        best = t1;
        normal.x = n1.x; normal.y = n1.y; normal.z = n1.z;
      }
    }
    const exit = Math.min(nextX, nextZ, leave);
    if (best <= exit || exit >= leave || (stepX === 0 && stepZ === 0)) break;
    if (nextX < nextZ) {
      i += stepX;
      if (i < 0 || i >= nx) break;
      nextX += deltaX;
    } else {
      j += stepZ;
      if (j < 0 || j >= nz) break;
      nextZ += deltaZ;
    }
  }
  if (best === Infinity) return null;
  const hx = origin.x + dir.x * best;
  const hz = origin.z + dir.z * best;
  const si = Math.min(nx - 1, Math.max(0, Math.floor((hx - terrain.origin.x) / cell)));
  const sj = Math.min(nz - 1, Math.max(0, Math.floor((hz - terrain.origin.z) / cell)));
  const surface = terrain.surfaces[terrain.surfaceIds[sj * nx + si] as number];
  return { dist: best, boxIndex: -1, normal, material: surface?.material ?? "dirt" };
}

function rayTriangle(
  o: Vec3,
  dir: Vec3,
  a: Vec3,
  b: Vec3,
  c: Vec3,
  maxDist: number,
  normal: Vec3,
): number | null {
  const e1x = b.x - a.x, e1y = b.y - a.y, e1z = b.z - a.z;
  const e2x = c.x - a.x, e2y = c.y - a.y, e2z = c.z - a.z;
  const px = dir.y * e2z - dir.z * e2y;
  const py = dir.z * e2x - dir.x * e2z;
  const pz = dir.x * e2y - dir.y * e2x;
  const det = e1x * px + e1y * py + e1z * pz;
  if (Math.abs(det) < 1e-10) return null;
  const inv = 1 / det;
  const tx = o.x - a.x, ty = o.y - a.y, tz = o.z - a.z;
  const u = (tx * px + ty * py + tz * pz) * inv;
  if (u < 0 || u > 1) return null;
  const qx = ty * e1z - tz * e1y;
  const qy = tz * e1x - tx * e1z;
  const qz = tx * e1y - ty * e1x;
  const v = (dir.x * qx + dir.y * qy + dir.z * qz) * inv;
  if (v < 0 || u + v > 1) return null;
  const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
  if (t < 0 || t > maxDist) return null;
  let nx = e1y * e2z - e1z * e2y;
  let ny = e1z * e2x - e1x * e2z;
  let nz = e1x * e2y - e1y * e2x;
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len; ny /= len; nz /= len;
  if (nx * dir.x + ny * dir.y + nz * dir.z > 0) { nx = -nx; ny = -ny; nz = -nz; }
  normal.x = nx; normal.y = ny; normal.z = nz;
  return t;
}

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Clamps `v` strictly INSIDE [lo, hi] by `SEAM_PROBE`, so a seam probe cannot
 * land exactly on a box edge; degenerate ranges collapse to their midpoint.
 */
function insideRange(v: number, lo: number, hi: number): number {
  const a = lo + SEAM_PROBE;
  const b = hi - SEAM_PROBE;
  if (b <= a) return (lo + hi) * 0.5;
  return v < a ? a : v > b ? b : v;
}

function numericAsc(a: number, b: number): number {
  return a - b;
}
